import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './CreationStagePanel.module.css';
import { usePuzzleState } from '../../context/PuzzleStateContext';
import { useMetadata } from '../../context/MetadataContext';
import { useGlobalError } from '../../context/GlobalErrorContext';
import ConfirmationDialog from '../ConfirmationDialog';

const CreationStagePanel = () => {
  const { puzzleState, createRevert, validatePuzzle } = usePuzzleState();
  const { getText } = useMetadata();
  const { triggerError } = useGlobalError();
  
  // State for revert confirmation dialog
  const [showRevertConfirmation, setShowRevertConfirmation] = useState(false);

  // Don't render if no puzzle state
  if (!puzzleState) {
    triggerError({
      type: 'non-critical',
      message: 'CreationStagePanel component rendered without puzzle data. This indicates a routing/status determination error.'
    });
    return null;
  }

  const creationStage = puzzleState?.state?.creation_stage || 1;
  
  // Define the stages
  const stages = [
    { id: 1, key: 'set_values', label: getText('create.stage_1') },
    { id: 2, key: 'design_puzzle', label: getText('create.stage_2') },
    { id: 3, key: 'set_colors', label: getText('create.stage_3') }
  ];

  // Check if revert action is available
  const revertAction = puzzleState?.grid?.allowed_actions?.find(a => a.action === 'action_revert');
  const validateAction = puzzleState?.grid?.allowed_actions?.find(a => a.action === 'action_validate');

  const handleRevert = () => {
    if (revertAction?.enabled) {
      // Show confirmation dialog when reverting from stage 2 to stage 1
      // because user will lose all their cage and reveal definitions
      if (creationStage === 2) {
        setShowRevertConfirmation(true);
      } else {
        // No confirmation needed for stage 3 to stage 2
        createRevert();
      }
    }
  };

  const handleValidate = () => {
    if (validateAction?.enabled) {
      validatePuzzle();
    }
  };

  // Handle revert confirmation
  const handleRevertConfirm = () => {
    createRevert();
    setShowRevertConfirmation(false);
  };

  const handleRevertCancel = () => {
    setShowRevertConfirmation(false);
  };

  return (
    <>
      <div className={styles.creationStagePanel}>
        {/* Revert button */}
        <button
          type="button"
          className={`${styles.actionButton} ${styles.revertButton}`}
          disabled={!revertAction?.enabled}
          onClick={handleRevert}
          aria-label={getText('actions.action_revert')}
        >
          <ChevronLeft size={16} />
        </button>

        {/* Stage indicators */}
        <div className={styles.stageIndicators}>
          {stages.map((stage, index) => (
            <React.Fragment key={stage.id}>
              <div 
                className={`${styles.stageIndicator} ${
                  stage.id === creationStage ? styles.active : 
                  stage.id < creationStage ? styles.completed : styles.pending
                }`}
              >
                <div className={styles.stageNumber}>{stage.id}</div>
                <div className={styles.stageLabel}>{stage.label}</div>
              </div>
              {index < stages.length - 1 && (
                <div className={styles.stageSeparator}></div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Validate button */}
        <button
          type="button"
          className={`${styles.actionButton} ${styles.validateButton}`}
          disabled={!validateAction?.enabled}
          onClick={handleValidate}
          aria-label={getText('actions.action_validate')}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Revert confirmation dialog - only for stage 2 to 1 */}
      <ConfirmationDialog
        isOpen={showRevertConfirmation}
        onConfirm={handleRevertConfirm}
        onCancel={handleRevertCancel}
        title={getText('actions.action_revert')}
        message={getText('create.revert_stage2_confirmation')}
        confirmText={getText('common_text.yes')}
        cancelText={getText('common_text.no')}
      />
    </>
  );
};

export default CreationStagePanel;